/**
 * @file Weather Context Definition
 * @description The React context the header answer is shared through (W24 Job D): the frost bar's alert, the weather now,
 * the header pill's icon, the "no forecast yet" sentence and how long the phone keeps the answer, all the server's
 * (GET /header, api/header). The provider is WeatherContext.tsx (kept by its name); read with
 * shared/hooks/useWeatherContext.
 *
 * @module context/WeatherContextDefinition
 */

import { createContext } from 'react';
import type { HeaderAlert, HeaderAnswer, HeaderNow, SkyKey } from '../api/header';

/**
 * WeatherContextType interface
 * @interface WeatherContextType
 * @description The header answer as every place reads it: each part as the server sent it, nothing worked out here.
 */
export interface WeatherContextType {
  /** The whole answer, or undefined until it is in (or when it cannot be asked for yet). */
  header: HeaderAnswer | undefined;

  /** The frost bar's alert, the server's; null for none. */
  alert: HeaderAlert | null;

  /** The weather now, the server's one reading (B77) in the reader's unit and language; null when there is none. */
  now: HeaderNow | null;

  /** The icon the header's pill draws, the server's (the snowflake under a frost tonight, else the sky's); null for none. */
  pillIcon: SkyKey | null;

  /**
   * The server's sentence for a garden the weather service cannot place yet (B44), in the gardener's language;
   * null when there is a forecast, or while it loads.
   */
  noForecastMessage: string | null;

  /** How long the phone keeps the answer, in minutes (the server's keepMinutes); null until the first answer. */
  keepMinutes: number | null;

  /** Flag indicating if the header answer is currently being loaded */
  isLoading: boolean;

  /** Error object if there was a problem loading the header answer */
  error: Error | null;

  /** Function to read the header answer again */
  refetchWeather: () => Promise<unknown>;
}

/**
 * WeatherContext
 * @constant WeatherContext
 * @description React context for the header answer; undefined outside WeatherProvider.
 *
 * @type {React.Context<WeatherContextType | undefined>}
 */
export const WeatherContext = createContext<WeatherContextType | undefined>(undefined);
